import { useEffect } from 'react';

import { FeatureFlagApi, type FeatureFlag as FeatureFlagType } from 'apis/FeatureFlagApi';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useFeatureFlag } from 'pages/account/feature_flag/context';
import { helpContent } from 'pages/account/feature_flag/help';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { KendoIntlProvider } from 'app/culture';
import { useNavigate } from 'react-router-dom';
import { GMetrixTable } from 'components';
import { Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'pages/account/feature_flag/styles/main.scss';
import 'styles/global-styles.scss';

export const FeatureFlag = () => {

    const [state, setState] = useSuperState({
        featureFlags: [] as FeatureFlagType[],
        loadingGridData: true,
        searchTerm: ''
    });
    const { featureFlags, loadingGridData, searchTerm } = state;

    const { permissions } = useFeatureFlag();
    const { formatMessage, locale } = useIntl();
    const { catchWithToast } = useNotification();
    const navigate = useNavigate();

    const setSearchTerm = (searchTerm: string) => setState({ searchTerm });

    const fetchFeatureFlags = async () => {

        try {

            const featureFlags = await FeatureFlagApi.GetAll(searchTerm, []);

            setState({
                featureFlags,
                loadingGridData: false
            });

        } catch (e){

            catchWithToast(formatMessage({ id: 'failed_to_load_feature_flags.23436d43', defaultMessage: 'Failed to load feature flags.' }))(e);

        }

    };

    useEffect(() => {

        fetchFeatureFlags();

    }, [searchTerm]);

    return (
        <div>
            <KendoIntlProvider>
                <LocalizationProvider language={locale}>
                    <IntlProvider locale={locale}>
                        <div className="feature-flags d-flex flex-column gap-3">

                            <PageHeader
                                cta={{
                                    disabled: permissions.Create === false,
                                    hidden: permissions.Create === false,
                                    label: (
                                        <div className={'d-inline-flex align-items-center gap-1'}>
                                            <i className={'fa-solid fa-plus'} />
                                            <span>
                                                <FormattedMessage defaultMessage="New Feature Flag" id="new_feature_flag.cea7185a" />
                                            </span>
                                        </div>
                                    ),
                                    to: '/features/manage/new'
                                }}
                                helpContent={helpContent}
                                title={formatMessage({ id: 'feature_flags.b1263a5a', defaultMessage: 'Feature Flags' })}
                            />

                            <Card body>
                                <GMetrixTable
                                    allowExport
                                    columnData={[
                                        {
                                            field: 'Name',
                                            title: formatMessage({ id: 'flag.fea8fb19', defaultMessage: 'Flag' })
                                        },
                                        {
                                            field: 'DisplayName',
                                            title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })
                                        },
                                        {
                                            cellTitle: dataItem => dataItem.EarlyAccessRing.Description,
                                            field: 'CurrentRingName',
                                            title: formatMessage({ id: 'ring.0c682750', defaultMessage: 'Ring' })
                                        },
                                        {
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    <FormattedDate value={new Date(dataItem.CreateDate)} />
                                                </td>
                                            ),
                                            field: 'CreateDate',
                                            title: formatMessage({ id: 'created.8d9f8791', defaultMessage: 'Created' })
                                        },
                                        {
                                            field: 'Description'
                                        }

                                    ]}
                                    bottomBarTableName={formatMessage({ id: 'feature_flags.b1263a5a', defaultMessage: 'Feature Flags' })}
                                    loading={loadingGridData}
                                    onDetailsItemClick={permissions.Create ? (dataItem => navigate('/features/manage/' + dataItem.Id)) : undefined}
                                    rowData={featureFlags}
                                    searchPlaceholder={formatMessage({ id: 'search_feature_flags_by_name_or_ring.c526414b', defaultMessage: 'Search feature flags by name or ring...' })}
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    toggleColumns={false}
                                />
                            </Card>
                        </div>
                    </IntlProvider>
                </LocalizationProvider>
            </KendoIntlProvider>
        </div>
    );

};
